import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiAward, FiClock, FiShield } = FiIcons;

const About = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: FiAward,
      key: 'experience',
      value: '20+',
      color: 'text-blue-600'
    },
    {
      icon: FiClock,
      key: 'availability',
      value: '24/7',
      color: 'text-green-600'
    },
    {
      icon: FiShield,
      key: 'warranty',
      value: '100%',
      color: 'text-purple-600'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('about.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                {t('about.subtitle')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('about.description')}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-white p-4 rounded-2xl shadow-lg mb-4">
                    <SafeIcon icon={feature.icon} className={`w-8 h-8 mx-auto ${feature.color}`} />
                  </div>
                  <div className={`text-2xl font-bold ${feature.color} mb-2`}>
                    {feature.value}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t(`about.features.${feature.key}`)}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-2xl shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('about.areas.title')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                  <div className="text-3xl mb-2">🏖️</div>
                  <div className="font-bold text-gray-900">{t('about.areas.lamanga')}</div>
                  <div className="text-sm text-gray-600">{t('about.areas.lamangatext')}</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl">
                  <div className="text-3xl mb-2">⚓</div>
                  <div className="font-bold text-gray-900">{t('about.areas.cartagena')}</div>
                  <div className="text-sm text-gray-600">{t('about.areas.cartagenatext')}</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                  <div className="text-3xl mb-2">✈️</div>
                  <div className="font-bold text-gray-900">{t('about.areas.sanjavier')}</div>
                  <div className="text-sm text-gray-600">{t('about.areas.sanjaviertext')}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="space-y-4"
              >
                <img
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Reparación de ordenadores en La Manga del Mar Menor"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Técnico informático en Cartagena"
                  className="rounded-2xl shadow-lg w-full h-32 object-cover"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="space-y-4 pt-8"
              >
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Análisis de datos en San Javier"
                  className="rounded-2xl shadow-lg w-full h-32 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                  alt="Mantenimiento informático empresas Mar Menor"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
              </motion.div>
            </div>
            
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -top-6 -right-6 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg"
            >
              <span className="text-white font-bold">B</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;